/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { ChangeEvent, FormEvent, useState } from "react";
import { Modal, ModalOverlay, ModalContent, ModalBody } from "@chakra-ui/modal";
import Card from "components/card";
import InputField from "components/fields/InputField";
import RoleDropdown from "views/admin/user/components/dropdown/RoleDropdown";
import MerchantDropdown from "views/admin/user/components/dropdown/MerchantDropdown";
import AgentDropdown from "views/admin/user/components/dropdown/AgentDropdown";
import SubaccountDropdown from "views/admin/user/components/dropdown/SubaccountDropdown";
import { Button } from "@chakra-ui/react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  newUser: {
    username: any;
    email: string;
    role: string;
    merchant: string;
    agent: string;
    subaccount: string;
  };
  handleInputChange: (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  roleOptions: string[];
  merchants: { id: string; name: string }[] | null;
  agents: { id: string; name: string }[] | null;
  subaccounts: { id: string; suba_name: string }[] | null;
  resetNewUser: () => void;
  isSubmitting: boolean;
  isSetSubaccounts: boolean;
  setSubaccounts: any;
}

const CreateUserModal: React.FC<Props> = ({
  isOpen,
  onClose,
  handleSubmit,
  newUser,
  handleInputChange,
  roleOptions,
  merchants,
  agents,
  subaccounts,
  resetNewUser,
  isSubmitting,
  isSetSubaccounts,
  setSubaccounts,
}) => {
  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  const handleSubmitWithValidation = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      newUser.username &&
      newUser.email &&
      isValidEmail(newUser.email) &&
      newUser.role &&
      (
        newUser.role === "SuperAdmin" ||
        (newUser.role === "Agent" && newUser.agent) ||
        (newUser.role === "Merchant" && newUser.merchant) ||
        (newUser.role !== "SuperAdmin" && newUser.role !== "Agent" && newUser.role !== "Merchant" && newUser.merchant && newUser.subaccount)
      )
    ) {
      handleSubmit(e);
    }
  };

  const handleModalClose = () => {
    setSubaccounts([]);
    resetNewUser();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      closeOnOverlayClick={false}
    >
      <ModalOverlay className="bg-[#000] !opacity-30" />
      <ModalContent className="dark:!bg-neutral-900 !z-[1002] !m-auto !w-full max-w-xl !rounded-2xl !border !border-gray-200 !bg-white !shadow-xl dark:!border-white/10 md:!top-[12vh]">
        <ModalBody className="p-6 md:p-7">
          <div className="mb-4 flex items-start gap-3">
            <div className="rounded-xl bg-primary-50 p-2 text-primary-600 ring-1 ring-primary-100 dark:bg-primary-900/20 dark:text-primary-400 dark:ring-primary-900/30">
              <svg
                viewBox="0 0 24 24"
                className="h-5 w-5"
                fill="currentColor"
              >
                <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                Create New User
              </h1>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Add a new user to the system
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmitWithValidation}>
            <div className="mb-5">
              <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                Role <span className="text-red-500">*</span>
              </label>
              <RoleDropdown
                isDisabled={isSubmitting}
                newUser={newUser}
                handleInputChange={handleInputChange}
                roleOptions={roleOptions}
              />
            </div>

            {newUser.role !== "Agent" && newUser.role !== "SuperAdmin" &&
              <>
                <div className="mb-5">
                  <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                    Merchant <span className="text-red-500">*</span>
                  </label>
                  <MerchantDropdown
                    isDisabled={isSubmitting}
                    newUser={{ merchant: newUser.merchant }}
                    handleInputChange={handleInputChange}
                    merchants={merchants}
                  />
                </div>

                {newUser.role !== "Merchant" &&
                  <div className="mb-5">
                    <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                      Subaccount <span className="text-red-500">*</span>
                    </label>
                    <SubaccountDropdown
                      isDisabled={isSubmitting}
                      newUser={{ subaccount: newUser.subaccount }}
                      handleInputChange={handleInputChange}
                      merchants={merchants}
                      subaccounts={subaccounts}
                      isSetSubaccounts={isSetSubaccounts}
                    />
                  </div>
                }
              </>
            }

            {newUser.role === "Agent" &&
              <div className="mb-5">
                <label className="mb-1 block text-sm font-medium text-gray-800 dark:text-gray-100">
                  Agent <span className="text-red-500">*</span>
                </label>
                <AgentDropdown
                  isDisabled={isSubmitting}
                  newUser={{ agent: newUser.agent }}
                  handleInputChange={handleInputChange}
                  agents={agents}
                />
              </div>
            }

            <div className="mb-5">
              <InputField
                disabled={isSubmitting}
                variant="auth"
                extra="shadow-sm"
                label="Username"
                required={true}
                id="username"
                name="username"
                type="text"
                placeholder="Enter username"
                value={newUser.username}
                onChange={handleInputChange}
                autoComplete="off"
              />
            </div>

            <div className="mb-5">
              <InputField
                disabled={isSubmitting}
                variant="auth"
                extra="shadow-sm"
                label="Email"
                required={true}
                id="email"
                name="email"
                type="email"
                placeholder="Enter email"
                value={newUser.email}
                onChange={handleInputChange}
                autoComplete="off"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button
                onClick={handleModalClose}
                isDisabled={isSubmitting}
                className="dark:bg-neutral-900 dark:hover:bg-neutral-800 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-white/10 dark:text-white"
              >
                Cancel
              </Button>

              <Button
                type="submit"
                isDisabled={
                  !(
                    newUser.username &&
                    newUser.email &&
                    isValidEmail(newUser.email) &&
                    newUser.role &&
                    (
                      newUser.role === "SuperAdmin" ||
                      (newUser.role === "Agent" && newUser.agent) ||
                      (newUser.role === "Merchant" && newUser.merchant) ||
                      (newUser.role !== "SuperAdmin" && newUser.role !== "Agent" && newUser.role !== "Merchant" && newUser.merchant && newUser.subaccount)
                    )
                  ) || isSubmitting
                }
                isLoading={isSubmitting}
                loadingText="Creating User..."
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 disabled:cursor-not-allowed disabled:bg-gray-400"
              >
                Create
              </Button>
            </div>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CreateUserModal;